import { useEffect, useState } from 'react';
import { LoaderOverlay } from '@common-components';
import { BrandingMode } from 'enums';
import { useBoolean, useGetMarketingUpdateTableHtml, useMatchNavigation } from 'hooks';
import { messages } from 'i18n';
import { BROKER_NESTED_ROUTES, MarketingUpdateRoutes } from 'broker/broker-routes';
import WithRouteLocationState from 'broker/components/WithRouteLocationState';
import { RouteKey } from 'broker/enums';
import { useNavigate, useNavigateBack } from 'broker/hooks';
import InnerPageLayout from 'broker/pages/SubmissionWorkspacePage/components/InnerPage/InnerPageLayout';
import { InnerPageProps } from 'broker/pages/SubmissionWorkspacePage/components/InnerPage/types';
import { PdfGenerationStatus } from 'broker/pages/SubmissionWorkspacePage/components/NestedViews/QuoteComparisonInsights/types';
import { useWorkspaceUrls } from 'broker/pages/SubmissionWorkspacePage/hooks';
import useSubmissionsWorkspace from 'broker/pages/SubmissionWorkspacePage/store/useSubmissionWorkspace';
import Footer from './components/Footer';
import Steps from './components/Steps';

export default function MarketingUpdate() {
  const navigate = useNavigate();
  const { partialSubmission, updateSubmission } = useSubmissionsWorkspace();
  const { html: htmlTable, isLoading: isTableLoading } = useGetMarketingUpdateTableHtml(partialSubmission?.id);
  const { marketingWorkspaceUrl, marketingUpdateEmailUrl } = useWorkspaceUrls();
  const [pdfGenerationStatus, setPdfGenerationStatus] = useState<PdfGenerationStatus>(PdfGenerationStatus.Idle);
  const currentStep = useMatchNavigation(MarketingUpdateRoutes, MarketingUpdateRoutes.Compose);
  const [editorContent, setEditorContent] = useState<string | undefined>(
    partialSubmission?.draftMarketingUpdate?.pdfContent || '',
  );

  // Update editor content when table HTML becomes available (if no saved draft exists)
  useEffect(() => {
    const hasSavedDraft = !!partialSubmission?.draftMarketingUpdate?.pdfContent;
    const hasNoContent = !editorContent || editorContent === '';

    if (!hasSavedDraft && htmlTable && hasNoContent) {
      setEditorContent(htmlTable);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [htmlTable, partialSubmission?.draftMarketingUpdate?.pdfContent]);
  const [pdfFileId, setPdfFileId] = useState<string | undefined>(partialSubmission?.draftMarketingUpdate?.pdfFileId);
  const [isSubmitting, { on: startSubmit, off: stopSubmit }] = useBoolean(false);
  const initialPdfFileId = partialSubmission?.draftMarketingUpdate?.pdfFileId;
  const navigateBack = useNavigateBack();
  const [brandingMode, setBrandingMode] = useState<BrandingMode | undefined>(undefined);

  const saveForLater = async (fileId?: string) => {
    startSubmit();
    await updateSubmission(partialSubmission!.id, {
      draftMarketingUpdate: {
        ...partialSubmission?.draftMarketingUpdate,
        pdfContent: editorContent,
        ...(fileId && { pdfFileId: fileId }),
      },
    });
    stopSubmit();
  };

  const navigateToMarketingUpdateEmail = () => {
    if (!pdfFileId) {
      return;
    }
    navigate(marketingUpdateEmailUrl, {
      routeKey: RouteKey.MarketingUpdateEmail,
      state: {
        pdfFileId,
      },
    });
  };

  if (!partialSubmission) {
    return null;
  }

  const showLoaderOverlay = isTableLoading && !partialSubmission?.draftMarketingUpdate?.pdfContent;

  return (
    <WithRouteLocationState routeKey={RouteKey.MarketingUpdate} isStateRequired>
      {(locationState) => (
        <InnerPageLayout
          title={messages.marketingUpdate.title}
          beforeNavigation={async (transition) =>
            transition.location.pathname.includes(BROKER_NESTED_ROUTES.WORKSPACE.MARKETING_UPDATE)
          }
          onCloseOverride={async () => {
            if (locationState.omitEmailStep) {
              navigateBack();
              return;
            }
            navigate(marketingWorkspaceUrl, { routeKey: RouteKey.Marketing, state: {} });
          }}
          footer={(innerPageProps: InnerPageProps) => (
            <Footer
              omitEmailStep={!!locationState.omitEmailStep}
              currentStep={currentStep}
              onSaveForLater={saveForLater}
              isSubmitting={isSubmitting}
              pdfGenerationStatus={pdfGenerationStatus}
              navigateToMarketingUpdateEmail={navigateToMarketingUpdateEmail}
              editorContent={editorContent}
              setBrandingMode={setBrandingMode}
              pdfFileId={pdfFileId}
              submission={partialSubmission}
              {...innerPageProps}
            />
          )}
        >
          {(innerPageProps: InnerPageProps) => (
            <>
              {showLoaderOverlay && <LoaderOverlay />}
              <Steps
                editorContent={editorContent}
                setEditorContent={setEditorContent}
                pdfGenerationStatus={pdfGenerationStatus}
                setPdfGenerationStatus={setPdfGenerationStatus}
                submission={partialSubmission}
                pdfFileId={pdfFileId}
                setPdfFileId={setPdfFileId}
                initialPdfFileId={initialPdfFileId}
                brandingMode={brandingMode}
                htmlTable={htmlTable}
                isTableLoading={isTableLoading}
                {...innerPageProps}
              />
            </>
          )}
        </InnerPageLayout>
      )}
    </WithRouteLocationState>
  );
}
